import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { combineLatest, Observable, Subscription } from 'rxjs';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { map, take } from 'rxjs/operators';

import { Category, FinnairBoundItem, FinnairOrder } from '@fcom/dapi/api/models';
import { ButtonPosition, ButtonTheme } from '@fcom/ui-components/index';
import { Amount, IdAndHash } from '@fcom/dapi/index';
import { containsUnpaidSeats } from '@fcom/common-booking/utils/ancillaries.utils';
import { finShare } from '@fcom/rx/index';
import { FinnairServiceItemWithIcon } from '@fcom/common-booking/interfaces';

enum Action {
  CONFIRM,
  PROCEED_TO_PAYMENT,
}

interface CtaAction {
  key: string;
  action: Action;
}

@Component({
  selector: 'fin-check-in-know-your-trip',
  templateUrl: 'check-in-know-your-trip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInKnowYourTripComponent implements OnInit, OnDestroy {
  @Input()
  services$: Observable<FinnairServiceItemWithIcon[]>;

  @Input()
  bound$: Observable<FinnairBoundItem>;

  @Input()
  order$: Observable<FinnairOrder>;

  @Input()
  hasPendingSeats: boolean;

  @Output()
  confirmStep: EventEmitter<void> = new EventEmitter();

  @Output()
  viewSeatmap: EventEmitter<void> = new EventEmitter();

  @Output()
  clearPendingSeats: EventEmitter<void> = new EventEmitter();

  @Output()
  initializePayment: EventEmitter<IdAndHash> = new EventEmitter<IdAndHash>();

  ctaAction$: Observable<CtaAction>;
  totalPrice$: Observable<Amount>;
  isCarrierConnect$: Observable<boolean>;
  includeSeatActionButtons$: Observable<boolean>;

  private subscriptions = new Subscription();

  protected readonly ButtonTheme = ButtonTheme;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly ButtonPosition = ButtonPosition;

  ngOnInit(): void {
    this.totalPrice$ = this.order$.pipe(
      map(
        (order) =>
          order.services.unpaid?.find((serviceItem) => serviceItem.category === Category.SEAT)?.totalPrice || {
            amount: '0.0',
            currencyCode: order.prices?.included?.total?.total?.totalAmount?.currencyCode,
          }
      ),
      finShare()
    );
    this.ctaAction$ = this.totalPrice$.pipe(
      map((price) => {
        if (!(isNaN(+price?.amount) || +price?.amount === 0)) {
          return {
            key: 'MMB.checkIn.funnel.steps.kyt.button.proceedToPayment',
            action: Action.PROCEED_TO_PAYMENT,
          };
        }
        return {
          key: 'MMB.checkIn.funnel.steps.kyt.button.confirmStep',
          action: Action.CONFIRM,
        };
      })
    );
    this.isCarrierConnect$ = combineLatest([this.order$, this.bound$.pipe(map((bound) => bound.id))]).pipe(
      map(
        ([order, boundId]) =>
          order.eligibilities?.checkIn?.find((eligibility) => eligibility.id === boundId)?.isCarrierConnect ?? false
      )
    );
    this.includeSeatActionButtons$ = combineLatest([this.services$, this.bound$]).pipe(
      map(([services, bound]) => {
        const seats = services.find((s) => s.category === Category.SEAT);
        return seats?.bounds
          ?.find((b) => b.id === bound.id)
          ?.segments?.flatMap((s) => s.passengers?.flatMap((p) => p.services))
          .some((seat) => !!seat.seatNumber ?? false);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  continue(): void {
    this.subscriptions.add(
      this.order$.pipe(take(1)).subscribe((order) => {
        this.initializePaymentIfNeeded(order);
      })
    );
  }

  private initializePaymentIfNeeded(order: FinnairOrder): void {
    if (containsUnpaidSeats(order)) {
      this.initializePayment.emit({ id: order.id, hash: order.hash });
    } else {
      this.confirmStep.emit();
    }
  }
}
